import { _ } from "../../../../core/polyglot/polyglot.js";
import { required } from "../../../../core/widgets/forms/validators.js";
import { select } from "../../../../core/elements/index.js";
import { widget } from "@foretagsplatsen/widgetjs";
import dialog from "../../../../core/widgets/dialog.js";
import FormBuilder from "../../../../core/widgets/forms/FormBuilder.js";
import KeyFigureCategory from "../../../../models/keyFigure/KeyFigureCategory.js";
import KeyFigurePreset from "../../../../models/keyFigure/preset/KeyFigurePreset.js";

/**
 * `keyFigurePresetPickerWidget` displays buttons to save, edit or delete
 * the currently selected preset.
 *
 * @param {object} spec - Parameter object
 * @param {boolean} spec.enableEditor - Defines if the current user has admin rights on the current company
 * @param {object} spec.preset - the currently selected preset
 * @param {object} my - protected fields holder
 * @return {*}
 */
function keyFigurePresetPickerWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);
	const enableEditor = spec.enableEditor;
	let preset = spec.preset;

	my.customPreset = spec.customPreset;

	that.onPresetChange = my.events.createEvent();
	that.onPresetCreated = my.events.createEvent();
	that.onPresetEdited = my.events.createEvent();
	that.onPresetRemoved = my.events.createEvent();

	my.presetItems = [];
	my.selectedPreset = undefined;

	my.savePresetDialog = dialog({
		header: _("Save Preset"),
		body: renderSavePresetBodyOn,
		footer: renderSaveFooterOn,
	});

	my.deleteDialog = dialog({
		header: _("Delete Preset"),
		body: function (html) {
			html.p(
				_(
					"You are about to definitely delete the preset {preset}. Are you sure about this?",
					{
						preset: html.b(preset.getName()),
					},
				),
			);
		},
		footer: function (html) {
			html.button({ klass: "btn btn-default" }, _("Cancel")).click(
				my.deleteDialog.hide,
			);

			html.button({ klass: "btn btn-danger" }, _("Delete preset")).click(
				() => {
					my.deleteDialog.hide();
					that.onPresetRemoved.trigger(preset);
					that.update();
				},
			);
		},
	});

	let savePresetForm;

	that.getPresetItem = function (presetId) {
		const items = that.getPresets();
		const id = presetId;

		let selectedItem;
		let i = 0;
		const size = items.length;

		while (!selectedItem && i < size) {
			const item = items[i++];
			if (item.id === id) {
				selectedItem = item;
			}
		}
		return selectedItem;
	};

	that.setCustomPreset = function (customPreset) {
		my.customPreset = customPreset;
	};

	that.selectPreset = function (newPreset) {
		preset = newPreset;
		if (!preset) {
			my.selectedPreset = null;
			that.update();
			return;
		}

		if (preset.getId() === my.customPreset.getId()) {
			my.ensureCustomPresetInDropdown();
		} else {
			my.removeCustomPresetFromDropdown();
		}

		my.selectedPreset = preset;
		that.update();
	};

	that.getSelected = function () {
		return my.selectedPreset;
	};

	that.setPresets = function (presets, selectedPresetId) {
		const dropdownElements = [];
		const presetCategories = {};
		let selectedDropdownItem;

		presets
			.sort((a, b) =>
				a.getName() > b.getName()
					? 1
					: // eslint-disable-next-line sonarjs/no-nested-conditional -- we should fix this
						a.getName() < b.getName()
						? -1
						: 0,
			)
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			.forEach((preset) => {
				presetCategories[preset.getName()] = new KeyFigureCategory({
					name: preset.getName(),
					items: preset.getKeyFigures(),
					group: _("Presets"),
				});

				const dropdownItem = {
					label: preset.getName(),
					value: preset,
					id: preset.getId(),
				};

				if (preset.getId() === (selectedPresetId || "MyKeyFigures")) {
					selectedDropdownItem = dropdownItem;
				}

				selectedDropdownItem ||= dropdownItem;

				dropdownElements.push(dropdownItem);
			});

		my.presetItems = dropdownElements;
		that.selectPreset(selectedDropdownItem.value);

		return presetCategories;
	};

	that.getPresets = function () {
		return my.presetItems;
	};

	that.renderContentOn = function (html) {
		my.renderPresetsPickerOn(html);
		html.div({ klass: "clear" });
		that.renderEditorOn(html);
	};

	my.renderPresetsPickerOn = function (html) {
		if (!my.presetItems.length) {
			return;
		}

		html.render(
			select({
				items: my.presetItems,
				selected: my.selectedPreset,
				onChange: (selectedPreset) => {
					that.selectPreset(selectedPreset);
					that.onPresetChange.trigger(selectedPreset);
				},
			}),
		);
	};

	that.renderEditorOn = function (html) {
		if (!enableEditor || !preset) {
			return;
		}
		if (preset.isCustom() && preset.getKeyFigures().length) {
			renderSaveButtonOn(html);
		}
		if (!preset.isSystemPreset() && !preset.isCustom()) {
			renderEditButtonsOn(html);
		}
	};

	function renderSaveButtonOn(html) {
		html.a(
			{
				klass: "btn btn-default btn-xs preset-btn pull-right",
				"data-test": "save",
			},
			_("Save"),
		).click(my.savePresetDialog.show);
	}

	function renderEditButtonsOn(html) {
		html.div(
			html.a(
				{
					href: my.linkTo("edit-preset", { id: preset.getId() }),
					klass: "btn btn-default btn-xs preset-btn edit-preset-btn",
				},
				_("Edit"),
			),

			html
				.a(
					{
						klass: "btn btn-default btn-xs preset-btn delete-preset-btn",
					},
					_("Delete"),
				)
				.click(my.deleteDialog.show),
		);
	}

	function renderSavePresetBodyOn(html) {
		savePresetForm = buildSaveForm(createPreset);
		html.render(savePresetForm);
	}

	function renderSaveFooterOn(html) {
		html.button({ klass: "btn btn-default" }, _("Cancel")).click(
			my.savePresetDialog.hide,
		);

		html.button(
			{ klass: "btn btn-primary", "data-test": "ok" },
			_("Save preset"),
		).click((...rest) => savePresetForm.submit(...rest));
	}

	my.ensureCustomPresetInDropdown = function () {
		const elements = my.presetItems;
		let dropdownItem = that.getPresetItem(my.customPreset.getId());

		if (!dropdownItem) {
			dropdownItem = {
				name: my.customPreset.getName(),
				value: my.customPreset,
				id: my.customPreset.getId(),
			};

			elements.push(dropdownItem);
			my.presetItems = elements;
		}
	};

	my.removeCustomPresetFromDropdown = function () {
		my.presetItems = my.presetItems.filter(
			(element) => element.id !== my.customPreset.getId(),
		);
	};

	function buildSaveForm(submitCallback) {
		const form = new FormBuilder({
			actionsClass: "modal-footer",
			saveAction: submitCallback,
			saveLabel: _("Save preset"),
			showCancel: true,
			showActions: false,
			cancelAction: my.savePresetDialog.hide,
			labelClass: "col-sm-3",
			inputClass: "col-sm-9",
		});

		form.input({
			placeHolder: _("Profit Key Figures"),
			label: _("Name"),
			accessor: function () {
				return "";
			},
			mutator: (name) => preset.setName(name),
			validator: required(),
			name: "name",
		});

		form.input({
			label: _("Description"),
			accessor: function () {
				return "";
			},
			mutator: (description) => preset.setDescription(description),
			placeHolder: _("All Key Figures related to profits"),
			validator: required(),
			name: "preset",
		});

		return form;
	}

	function createPreset() {
		// Create a new preset to be saved from the custom preset data that
		// is actually not a custom preset.
		const newPreset = new KeyFigurePreset({
			name: preset.getName(),
			description: preset.getDescription(),
			keyFigures: preset.getKeyFigures(),
		});
		my.savePresetDialog.hide();
		that.onPresetCreated.trigger(newPreset);
		that.update();
	}

	return that;
}

export default keyFigurePresetPickerWidget;
